<template>
  <v-container>
    <div
      v-if="!checkIfPermission('create default data', admin.permissions)"
      class="w-100 h-100 d-flex justify-center align-center"
    >
      <h1 class="error--text">You don't have access to this page</h1>
    </div>
    <div v-else>
      <h1>System Settings</h1>
      <v-row>
        <v-col>
          <v-card flat>
            <v-card-title>System Settings</v-card-title>
            <v-card-subtitle
              >Modify platform general settings here
            </v-card-subtitle>
            <v-card-text>
              <!-- Section jump list -->
              <div class="mb-4">
                <v-chip
                  v-for="section in sections"
                  :key="section.id"
                  class="mr-2 mb-2"
                  small
                  outlined
                  @click="goToSection(section.id)"
                >
                  {{ section.title }}
                </v-chip>
              </div>

              <!-- Basic Platform Settings -->
              <v-divider class="mb-4"></v-divider>
              <h3 id="basic-platform" class="mb-4">Basic Platform Settings</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    v-model="siteSettings.currency"
                    hint="Platform base currency"
                    persistent-hint
                    label="System Currency"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    v-model="siteSettings.currency_sym"
                    hint="Platform base currency symbol"
                    persistent-hint
                    label="System Currency Symbol"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Customer Relation Contact Phone Number"
                    v-model="siteSettings.phone"
                    persistent-hint
                    label="Contact Number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Contact Email"
                    v-model="siteSettings.email"
                    persistent-hint
                    label="Platform Email Address"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Platform timezone"
                    v-model="siteSettings.timezone"
                    persistent-hint
                    label="Timezone"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    filled
                    :disabled="loading"
                    v-model="siteSettings.paymentProvider"
                    hint="Select payment provider for transactions"
                    persistent-hint
                    label="Payment Provider"
                    :items="[
                      { text: 'Redbiller', value: 'redbiller' },
                      { text: 'PalmPay', value: 'palmpay' }
                    ]"
                    item-text="text"
                    item-value="value"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    dense
                    auto-grow
                    filled
                    :disabled="loading"
                    hint="Office Address"
                    v-model="siteSettings.address"
                    persistent-hint
                    label="Office Address"
                  ></v-textarea>
                </v-col>
              </v-row>

              <!-- Social Media Links -->
              <v-divider class="mb-4 mt-6"></v-divider>
              <h3 id="social-media" class="mb-4">Social Media Links</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Platform facebook link"
                    v-model="siteSettings.facebook"
                    persistent-hint
                    label="Facebook Link"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Platform Instagram Link"
                    v-model="siteSettings.instagram"
                    persistent-hint
                    label="Instagram Link"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Platform Twitter Link"
                    v-model="siteSettings.twitter"
                    persistent-hint
                    label="Twitter Link"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Platform Google/YouTube Link"
                    v-model="siteSettings.google"
                    persistent-hint
                    label="Google/YouTube Link"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Transaction Settings -->
              <v-divider class="mb-4 mt-6"></v-divider>
              <h3 id="transaction" class="mb-4">Transaction Settings</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    suffix="NGN"
                    filled
                    :disabled="loading"
                    v-model="siteSettings.ref"
                    persistent-hint
                    label="Referral Bonus"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Minimum Bonus Conversion"
                    persistent-hint
                    label="Minimum Bonus Conversion"
                    v-model="siteSettings.minbonus"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Business Profit Amount that charge VAT on"
                    persistent-hint
                    v-model="siteSettings.bizprofit"
                    label="Exchange Profit Amount"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Buy &amp; Sell Minimum Trade Amount in USD"
                    v-model="siteSettings.trademinimum"
                    persistent-hint
                    label="Minimum Trade Amount"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    suffix="%"
                    filled
                    :disabled="loading"
                    v-model="siteSettings.transcharge"
                    hint="Buy &amp; Sell Transaction Charge in %"
                    persistent-hint
                    label="Transaction Charge(VAT)"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Sending charge"
                    persistent-hint
                    v-model="siteSettings.sending_charge"
                    label="Sending Charge"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Decimal places for calculations"
                    persistent-hint
                    v-model="siteSettings.decimal"
                    label="Decimal Places"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Exchange rate"
                    persistent-hint
                    v-model="siteSettings.rate"
                    label="Exchange Rate"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Crypto Settings -->
              <v-divider class="mb-4 mt-6"></v-divider>
              <h3 id="crypto" class="mb-4">Crypto Settings</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Maximum daily crypto purchase limit"
                    persistent-hint
                    v-model="siteSettings.maxDailyCryptoPurchase"
                    label="Max Daily Crypto Purchase"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Maximum daily crypto deposit limit"
                    persistent-hint
                    v-model="siteSettings.maxDailyCryptoDeposit"
                    label="Max Daily Crypto Deposit"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Maximum daily crypto withdrawal limit"
                    persistent-hint
                    v-model="siteSettings.maxDailyCryptoWithdrawal"
                    label="Max Daily Crypto Withdrawal"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Maximum daily fiat withdrawal limit"
                    persistent-hint
                    v-model="siteSettings.maxDailyFiatWithdrawal"
                    label="Max Daily Fiat Withdrawal"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Token Settings -->
              <v-divider class="mb-4 mt-6"></v-divider>
              <h3 id="token" class="mb-4">Token Settings</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Token rate"
                    persistent-hint
                    v-model="siteSettings.tokenRate"
                    label="Token Rate"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Amount of snappy token given to users per transaction"
                    v-model="siteSettings.tokenBonusAmount"
                    persistent-hint
                    label="Token Amount"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Enable bonus system"
                    v-model="siteSettings.bonus"
                    persistent-hint
                    label="Bonus System"
                  ></v-switch>
                </v-col>
              </v-row>

              <!-- Referral Settings -->
              <v-divider class="mb-4 mt-6"></v-divider>
              <h3 id="referral" class="mb-4">Referral Settings</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    suffix="%"
                    filled
                    :disabled="loading"
                    hint="Percentage of Referral per transaction in %"
                    v-model="siteSettings.referPercentage"
                    persistent-hint
                    label="Referral Percentage"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Enable referral commission"
                    v-model="siteSettings.refcom"
                    persistent-hint
                    label="Referral Commission"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Enable referral buy bonus"
                    v-model="siteSettings.refbuy"
                    persistent-hint
                    label="Referral Buy Bonus"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Enable referral sell bonus"
                    v-model="siteSettings.refsell"
                    persistent-hint
                    label="Referral Sell Bonus"
                  ></v-switch>
                </v-col>
              </v-row>

              <!-- App Version Settings -->
              <v-divider class="mb-4 mt-6"></v-divider>
              <h3 id="app-version" class="mb-4">App Version Settings</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Minimum Android Version"
                    v-model="siteSettings.minimumAndroidVersion"
                    persistent-hint
                    label="Minimum Android Version"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Minimum Ios Version"
                    v-model="siteSettings.minimumIosVersion"
                    persistent-hint
                    label="Minimum Ios Version"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Feature Availability Settings -->
              <v-divider class="mb-4 mt-6"></v-divider>
              <h3 id="feature-availability" class="mb-4">Feature Availability Settings</h3>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    dense
                    auto-grow
                    filled
                    :disabled="loading"
                    hint="Office Address"
                    v-model="siteSettings.address"
                    persistent-hint
                    label="Office Address"
                  ></v-textarea>
                </v-col>
                <!-- <v-col cols="12" md="6">
                <v-text-field
                  suffix="NGN"
                  filled
                  :disabled="loading"
                  hint="Bonus amount for user when there referral completes KYC verification. Value in N"
                  persistent-hint
                  label="Referral Bonus"
                ></v-text-field>
              </v-col> -->
                <!-- <v-col cols="12" md="6">
                <v-text-field
                  filled
                  :disabled="loading"
                  hint="What user earn daily"
                  persistent-hint
                  label="Daily Bonus Earning"
                ></v-text-field>
              </v-col> -->
                <v-col cols="12" md="6">
                  <v-text-field
                    suffix="NGN"
                    filled
                    :disabled="loading"
                    v-model="siteSettings.ref"
                    persistent-hint
                    label="Referral Bonus"
                  ></v-text-field>
                </v-col>
                <!-- <v-col cols="12" md="6">
                <v-text-field
                  suffix="NGN"
                  filled
                  :disabled="loading"
                  hint="% earning on successful sell transaction (This value x USD Amount = Bonus earning in NGN)"
                  persistent-hint
                  label="Referral Sell Bonus"
                ></v-text-field>
              </v-col> -->
                <!-- <v-col cols="12" md="6">
                <v-text-field
                  suffix="USD"
                  filled
                  :disabled="loading"
                  hint="Maximum Amount Only Email Verified Members can Buy in USD"
                  v-model="siteSettings.nokyclimit"
                  persistent-hint
                  label="Level 1 Verified Members Limit"
                ></v-text-field>
              </v-col> -->
                <!-- <v-col cols="12" md="6">
                <v-text-field
                  suffix="USD"
                  filled
                  :disabled="loading"
                  hint="Maximum Amount Only Email and KYC Verified Members can Buy in USD"
                  persistent-hint
                  label="Level 2 Verified Members Limit"
                ></v-text-field>
              </v-col> -->
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Minimum Bonus Conversion"
                    persistent-hint
                    label="Minimum Bonus Conversion"
                    v-model="siteSettings.minbonus"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Business Profit Amount that charge VAT on"
                    persistent-hint
                    v-model="siteSettings.bizprofit"
                    label="Exchange Profit Amount"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Buy &amp; Sell Minimum Trade Amount in USD"
                    v-model="siteSettings.trademinimum"
                    persistent-hint
                    label="Minimum Trade Amount"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    suffix="%"
                    filled
                    :disabled="loading"
                    v-model="siteSettings.transcharge"
                    hint="Buy &amp; Sell Transaction Charge in %"
                    persistent-hint
                    label="Transaction Charge(VAT)"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    suffix="MINS"
                    filled
                    :disabled="loading"
                    hint="Minimum confirmation time for giftcard sale"
                    v-model="siteSettings.giftcardResponseTime"
                    persistent-hint
                    label="Response Time"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    suffix="%"
                    filled
                    :disabled="loading"
                    hint="Percentage of Referral per transaction in %"
                    v-model="siteSettings.referPercentage"
                    persistent-hint
                    label="Referral Percentage"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Amount of snappy token given to users per transaction"
                    v-model="siteSettings.tokenBonusAmount"
                    persistent-hint
                    label="Token Amount"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Minimum Android Version"
                    v-model="siteSettings.minimumAndroidVersion"
                    persistent-hint
                    label="Minimum Android Version"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Minimum Ios Version"
                    v-model="siteSettings.minimumIosVersion"
                    persistent-hint
                    label="Minimum Ios Version"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.giftcardFeatureAvailable"
                    persistent-hint
                    label="Giftcard Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Giftcard transaction break"
                    v-model="siteSettings.giftcardFeatureMessage"
                    persistent-hint
                    label="Giftcard Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.cryptoFeatureAvailable"
                    persistent-hint
                    label="Crypto Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Crypto transaction break"
                    v-model="siteSettings.cryptoFeatureMessage"
                    persistent-hint
                    label="Crypto Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.airtimeFeatureAvailable"
                    persistent-hint
                    label="Airtime Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Airtime transaction break"
                    v-model="siteSettings.airtimeFeatureMessage"
                    persistent-hint
                    label="Airtime Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.dataFeatureAvailable"
                    persistent-hint
                    label="Data Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Data transaction break"
                    v-model="siteSettings.dataFeatureMessage"
                    persistent-hint
                    label="Data Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.cableFeatureAvailable"
                    persistent-hint
                    label="Cable Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Cable transaction break"
                    v-model="siteSettings.cableFeatureMessage"
                    persistent-hint
                    label="Cable Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.bettingFeatureAvailable"
                    persistent-hint
                    label="Betting Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Betting transaction break"
                    v-model="siteSettings.bettingFeatureMessage"
                    persistent-hint
                    label="Betting Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.electricityFeatureAvailable"
                    persistent-hint
                    label="Electricity Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Electricity transaction break"
                    v-model="siteSettings.electricityFeatureMessage"
                    persistent-hint
                    label="Electricity Feature Message"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.airtimeToCashFeatureAvailable"
                    persistent-hint
                    label="Airtime To Cash Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Electricity transaction break"
                    v-model="siteSettings.airtimeToCashFeatureMessage"
                    persistent-hint
                    label="Airtime to Cash Feature Message"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.withdrawalFeatureAvailable"
                    persistent-hint
                    label="Withdrawal Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Withdrawal transaction break"
                    v-model="siteSettings.withdrawalFeatureMessage"
                    persistent-hint
                    label="Withdrawal Feature Message"
                  ></v-text-field>
                </v-col>

                <v-col>
                  <v-text-field
                    filled
                    :disabled="loading"
                    type="date"
                    v-model="siteSettings.rankingStartDate"
                    persistent-hint
                    label="Ranking Start Date"
                  ></v-text-field>
                </v-col>

                <v-col>
                  <v-text-field
                    filled
                    :disabled="loading"
                    type="number"
                    v-model="siteSettings.signUpBonus"
                    persistent-hint
                    label="Sign Up Bonus"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    v-model="siteSettings.bettingRate"
                    persistent-hint
                    label="Betting Rate"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    v-model="siteSettings.minimumCashbackPoint"
                    persistent-hint
                    label="Minimum Cashback Point"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    v-model="siteSettings.cashbackNairaConversionRate"
                    persistent-hint
                    label="Cashback Naira Conversion Rate"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    v-model="siteSettings.cableRate"
                    persistent-hint
                    label="Cable Rate"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Electricity Rate"
                    v-model="siteSettings.electricityRate"
                    persistent-hint
                    label="Electricity Rate"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Airtime to Cash Rate"
                    v-model="siteSettings.airtimeToCashRate"
                    persistent-hint
                    suffix="%"
                    label="Airtime to Cash Rate in %"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Airtime Rate"
                    v-model="siteSettings.airtimeRate"
                    persistent-hint
                    suffix="%"
                    label="Airtime Rate in %"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Data Rate"
                    v-model="siteSettings.dataRate"
                    persistent-hint
                    suffix="%"
                    label="Data Rate in %"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Wallet Funding Bank Name"
                    v-model="siteSettings.walletFundingBankName"
                    persistent-hint
                    suffix="%"
                    label="Wallet Funding Bank Name"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Wallet Funding Account Name"
                    v-model="siteSettings.walletFundingAccountName"
                    persistent-hint
                    suffix="%"
                    label="Wallet Funding Account Name"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Wallet Funding Account Number"
                    v-model="siteSettings.walletFundingAccountNumber"
                    persistent-hint
                    suffix="%"
                    label="Wallet Funding Account Number"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.giftcardFeatureAvailable"
                    persistent-hint
                    label="Giftcard Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Giftcard transaction break"
                    v-model="siteSettings.giftcardFeatureMessage"
                    persistent-hint
                    label="Giftcard Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.cryptoFeatureAvailable"
                    persistent-hint
                    label="Crypto Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Crypto transaction break"
                    v-model="siteSettings.cryptoFeatureMessage"
                    persistent-hint
                    label="Crypto Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.airtimeFeatureAvailable"
                    persistent-hint
                    label="Airtime Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Airtime transaction break"
                    v-model="siteSettings.airtimeFeatureMessage"
                    persistent-hint
                    label="Airtime Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.dataFeatureAvailable"
                    persistent-hint
                    label="Data Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Data transaction break"
                    v-model="siteSettings.dataFeatureMessage"
                    persistent-hint
                    label="Data Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.cableFeatureAvailable"
                    persistent-hint
                    label="Cable Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Cable transaction break"
                    v-model="siteSettings.cableFeatureMessage"
                    persistent-hint
                    label="Cable Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.bettingFeatureAvailable"
                    persistent-hint
                    label="Betting Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Betting transaction break"
                    v-model="siteSettings.bettingFeatureMessage"
                    persistent-hint
                    label="Betting Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.electricityFeatureAvailable"
                    persistent-hint
                    label="Electricity Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Electricity transaction break"
                    v-model="siteSettings.electricityFeatureMessage"
                    persistent-hint
                    label="Electricity Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.airtimeToCashFeatureAvailable"
                    persistent-hint
                    label="Airtime To Cash Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Airtime to Cash transaction break"
                    v-model="siteSettings.airtimeToCashFeatureMessage"
                    persistent-hint
                    label="Airtime to Cash Feature Message"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Turn off to take a break"
                    v-model="siteSettings.withdrawalFeatureAvailable"
                    persistent-hint
                    label="Withdrawal Feature Available"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Message user will see when taking a Withdrawal transaction break"
                    v-model="siteSettings.withdrawalFeatureMessage"
                    persistent-hint
                    label="Withdrawal Feature Message"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Service Rates -->
              <v-divider class="mb-4 mt-6"></v-divider>
              <h3 id="service-rates" class="mb-4">Service Rates</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    v-model="siteSettings.bettingRate"
                    persistent-hint
                    label="Betting Rate"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Markup applied on top of the market rate when users buy crypto"
                    persistent-hint
                    v-model="siteSettings.cryptoMarkup"
                    label="Crypto Markup"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    v-model="siteSettings.cableRate"
                    persistent-hint
                    label="Cable Rate"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Markdown applied below the market rate when users sell crypto"
                    persistent-hint
                    v-model="siteSettings.cryptoMarkDown"
                    label="Crypto Mark Down"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Electricity Rate"
                    v-model="siteSettings.electricityRate"
                    persistent-hint
                    label="Electricity Rate"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Airtime to Cash Rate"
                    v-model="siteSettings.airtimeToCashRate"
                    persistent-hint
                    suffix="%"
                    label="Airtime to Cash Rate in %"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Airtime Rate"
                    v-model="siteSettings.airtimeRate"
                    persistent-hint
                    suffix="%"
                    label="Airtime Rate in %"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Data Rate"
                    v-model="siteSettings.dataRate"
                    persistent-hint
                    suffix="%"
                    label="Data Rate in %"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Spread applied on crypto-to-crypto swaps"
                    persistent-hint
                    v-model="siteSettings.cryptoSwapSpread"
                    label="Crypto Swap Spread"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Wallet Funding Settings -->
              <v-divider class="mb-4 mt-6"></v-divider>
              <h3 id="wallet-funding" class="mb-4">Wallet Funding Settings</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Wallet Funding Bank Name"
                    v-model="siteSettings.walletFundingBankName"
                    persistent-hint
                    label="Wallet Funding Bank Name"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Wallet Funding Account Name"
                    v-model="siteSettings.walletFundingAccountName"
                    persistent-hint
                    label="Wallet Funding Account Name"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Wallet Funding Account Number"
                    v-model="siteSettings.walletFundingAccountNumber"
                    persistent-hint
                    label="Wallet Funding Account Number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Fee charged on wallet deposits (interpreted as fixed amount or percentage, per Deposit Fee Type below)"
                    v-model="siteSettings.depositFee"
                    persistent-hint
                    label="Deposit Fee"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    filled
                    :disabled="loading"
                    v-model="siteSettings.depositFeeType"
                    hint="Whether Deposit Fee is a fixed amount or a percentage of the deposit"
                    persistent-hint
                    label="Deposit Fee Type"
                    item-text="text"
                    item-value="value"
                    :items="[
                      { text: 'Percentage', value: 'percentage' },
                      { text: 'Fixed', value: 'fixed' }
                    ]"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Maximum deposit fee that can be charged, regardless of Deposit Fee Type (0 = no cap)"
                    v-model="siteSettings.depositFeeCap"
                    persistent-hint
                    label="Deposit Fee Cap"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Cashback Settings -->
              <v-divider class="mb-4 mt-6"></v-divider>
              <h3 id="cashback" class="mb-4">Cashback Settings</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    v-model="siteSettings.minimumCashbackPoint"
                    persistent-hint
                    label="Minimum Cashback Point"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    v-model="siteSettings.cashbackNairaConversionRate"
                    persistent-hint
                    label="Cashback Naira Conversion Rate"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Additional Settings -->
              <v-divider class="mb-4 mt-6"></v-divider>
              <h3 id="additional" class="mb-4">Additional Settings</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    suffix="MINS"
                    hint="Minimum confirmation time for giftcard sale"
                    v-model="siteSettings.giftcardResponseTime"
                    persistent-hint
                    label="Response Time"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    type="date"
                    v-model="siteSettings.rankingStartDate"
                    persistent-hint
                    label="Ranking Start Date"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    type="number"
                    v-model="siteSettings.signUpBonus"
                    persistent-hint
                    label="Sign Up Bonus"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Enable stamp duty"
                    v-model="siteSettings.stampduty"
                    persistent-hint
                    label="Stamp Duty"
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="KYC mode"
                    v-model="siteSettings.kycmode"
                    persistent-hint
                    label="KYC Mode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    filled
                    :disabled="loading"
                    hint="Jost KYC ID"
                    v-model="siteSettings.jostkycid"
                    persistent-hint
                    label="Jost KYC ID"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    filled
                    :disabled="loading"
                    hint="Enable rank date changed"
                    v-model="siteSettings.rankDateChanged"
                    persistent-hint
                    label="Rank Date Changed"
                  ></v-switch>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions
              v-if="checkIfPermission('manage default data', admin.permissions)"
            >
              <v-spacer></v-spacer>
              <v-btn
                color="accent"
                @click="updateSiteData"
                :loading="loading"
                depressed
                width="150"
                class="font-weight-bold"
              >
                Save
              </v-btn>
            </v-card-actions>
          </v-card>

          <v-card flat class="mt-10" id="country-settings">
            <v-card-title>Country Settings</v-card-title>
            <v-card-text>
              <div>
                <v-select
                  :items="availableCountries"
                  label="Country"
                  v-model="selectedCountry"
                />
              </div>
              <div>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        v-model="countryValues.referralBonus"
                        persistent-hint
                        :label="`${selectedCountry} Referral Bonus`"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        hint="Minimum Bonus"
                        v-model="countryValues.minimumBonus"
                        persistent-hint
                        :label="`${selectedCountry} Minimum Bonus`"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        hint="Minimum Token"
                        v-model="countryValues.minimumToken"
                        persistent-hint
                        :label="`${selectedCountry} Minimum Token`"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        hint="Stamp Duty"
                        v-model="countryValues.stampDuty"
                        persistent-hint
                        :label="`${selectedCountry} Stamp Duty`"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        v-model="countryValues.usdRate"
                        persistent-hint
                        :label="`${selectedCountry} USD Rate`"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        v-model="countryValues.vat"
                        persistent-hint
                        :label="`${selectedCountry} Vat`"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        v-model="countryValues.tokenRate"
                        persistent-hint
                        label="Token Rate"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-select
                        filled
                        :disabled="loading"
                        v-model="countryValues.autoPayout"
                        persistent-hint
                        label="Auto Withdrawal"
                        item-text="text"
                        item-value="value"
                        :items="[
                          { text: 'Yes', value: 'true' },
                          { text: 'No', value: 'false' }
                        ]"
                      ></v-select>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        v-model="countryValues.fundingFee"
                        persistent-hint
                        label="Funding Fee"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        v-model="countryValues.fundingFeeMax"
                        persistent-hint
                        label="Funding Max Fee"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-select
                        filled
                        :disabled="loading"
                        v-model="countryValues.fundingFeeType"
                        persistent-hint
                        label="Funding Fee Type"
                        item-text="text"
                        item-value="value"
                        :items="[
                          { text: 'Percentage', value: 'percentage' },
                          { text: 'Fixed', value: 'fixed' }
                        ]"
                      ></v-select>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        v-model="countryValues.maximumDailyFunding"
                        persistent-hint
                        label="Maximum Daily Funding"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        v-model="countryValues.maximumFunding"
                        persistent-hint
                        label="Maximum Funding Limit"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        v-model="countryValues.minimumFunding"
                        persistent-hint
                        label="Minimum Funding"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        filled
                        :disabled="loading"
                        v-model="countryValues.maximumDailyWithdrawal"
                        persistent-hint
                        label="Maximum Daily Withdrawal"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions
                  v-if="
                    checkIfPermission(
                      'update country settings',
                      admin.permissions
                    )
                  "
                >
                  <v-spacer></v-spacer>
                  <v-btn
                    color="accent"
                    @click="updateCurrencySiteData"
                    :loading="loadingCurrency"
                    depressed
                    width="150"
                    class="font-weight-bold"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import dataApi from '../../../api/systemData'
import { mapState } from 'vuex'

export default {
  data() {
    return {
      loading: false,
      loadingCurrency: false,
      selectedCountry: 'NIGERIA',
      countryValues: {},
      sections: [
        { id: 'basic-platform', title: 'Basic Platform' },
        { id: 'social-media', title: 'Social Media' },
        { id: 'transaction', title: 'Transaction' },
        { id: 'crypto', title: 'Crypto' },
        { id: 'token', title: 'Token' },
        { id: 'referral', title: 'Referral' },
        { id: 'app-version', title: 'App Version' },
        { id: 'feature-availability', title: 'Feature Availability' },
        { id: 'service-rates', title: 'Service Rates' },
        { id: 'wallet-funding', title: 'Wallet Funding' },
        { id: 'cashback', title: 'Cashback' },
        { id: 'additional', title: 'Additional' },
        { id: 'country-settings', title: 'Country Settings' }
      ]
    }
  },
  computed: {
    ...mapState({
      siteSettings: (state) => state.admin.siteSettings,
      admin: (state) => state.admin.admin,
      countrySetting: (state) => state.admin.countrySetting,
      availableCountries() {
        return Object.keys(this.countrySetting).map((country) =>
          country.toUpperCase()
        )
      }
    })
  },
  watch: {
    selectedCountry(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.countryValues = this.countrySetting[
          this.selectedCountry?.toLocaleLowerCase()
        ]
      }
    }
  },
  created() {
    this.getSiteSettings()
    this.getCountrySetting()
  },
  methods: {
    goToSection(id) {
      this.$vuetify.goTo(`#${id}`, { offset: 80 })
    },
    async getSiteSettings() {
      const res = await dataApi.data().siteSettings()
      this.$store.commit('admin/setSiteSettings', res.data.data)
    },
    async updateSiteData() {
      this.loading = true
      const res = await dataApi.data().updateSite(this.siteSettings)
      if (res.error) {
        this.$store.dispatch('alert', {
          message:
            res.errorMessage.message ||
            `${res.internalError.message}, please try again`,
          status: true,
          error: true
        })
        return
      }
      this.$store.dispatch('alert', {
        message: 'Updated Successfully',
        status: true
      })
      this.loading = false
    },
    async getCountrySetting() {
      const res = await dataApi.data().countrySetting()
      this.$store.commit('admin/setCountrySetting', res.data.data)
      this.countryValues = this.countrySetting[
        this.selectedCountry?.toLocaleLowerCase()
      ]
      if (this.countryValues?.autoPayout !== '') {
        this.countryValues.autoPayout = this.countryValues.autoPayout.toString()
      }
    },
    async updateCurrencySiteData() {
      this.loadingCurrency = true
      const res = await dataApi.data().updateCountrySetting({
        ...this.countryValues,
        autoPayout: this.countryValues.autoPayout === 'true'
      })
      if (res.error) {
        this.$store.dispatch('alert', {
          message:
            res.errorMessage.message ||
            `${res.internalError.message}, please try again`,
          status: true,
          error: true
        })
        return
      }
      this.$store.dispatch('alert', {
        message: 'Updated Successfully',
        status: true
      })
      this.loadingCurrency = false
    }
  }
}
</script>

<style></style>
